import { useState, useRef, useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TiptapLink from "@tiptap/extension-link";
import CodeTiptap from "@tiptap/extension-code";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

/** Helper: get markdown string from editor via tiptap-markdown */
export const getEditorMarkdown = (editor) => {
  if (!editor) return "";
  return (editor.storage.markdown?.getMarkdown() || "").trim();
};

const TiptapInput = (props) => {
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [tempEntry, setTempEntry] = useState("");
  const editorCallbackFired = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        codeBlock: false,
        horizontalRule: false,
        hardBreak: {
          keepMarks: true,
        },
      }),
      CodeTiptap,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 dark:text-blue-400 underline cursor-pointer",
        },
      }),
      Placeholder.configure({
        placeholder: `Message ${props.selectedAgent?.name || "Hybrid"}...`,
      }),
      TiptapMarkdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    editorProps: {
      attributes: {
        class: [
          "w-full min-h-[56px] max-h-[50vh] overflow-y-auto px-1 py-1 text-[14px] leading-relaxed",
          "text-zinc-900 dark:text-zinc-100",
          "focus:outline-none",
          "prose prose-sm dark:prose-invert max-w-none",
        ].join(" "),
      },
      handleKeyDown: (view, event) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          const { editor: ed } = props.editorRef?.current || {};
          const md = ed ? getEditorMarkdown(ed) : "";
          if (md.trim()) {
            props.sendPayload(
              md,
              props.selectedAgent?.apiName,
              props.currentThreadId,
            );
            if (ed) {
              requestAnimationFrame(() => ed.commands.clearContent());
            }
          }
          return true;
        }

        // Arrow up/down for message history
        const history = props.sentHistory || [];
        if (history.length === 0) return false;

        const { state } = view;
        const { from, to } = state.selection;
        const atStart = from === 1 && to === 1;
        const atEnd = from === state.doc.content.size - 1;

        if (event.key === "ArrowUp" && atStart) {
          event.preventDefault();
          const idx = historyIndex;
          const { editor: ed } = props.editorRef?.current || {};
          if (idx === -1 && ed) {
            setTempEntry(getEditorMarkdown(ed));
          }
          const newIdx = Math.min(idx + 1, history.length - 1);
          setHistoryIndex(newIdx);
          props.setChatEntry(history[newIdx]);
          if (ed) {
            requestAnimationFrame(() => {
              ed.commands.setContent(history[newIdx]);
            });
          }
          return true;
        }
        if (event.key === "ArrowDown" && atEnd) {
          event.preventDefault();
          const idx = historyIndex;
          const { editor: ed } = props.editorRef?.current || {};
          if (idx <= 0) {
            setHistoryIndex(-1);
            props.setChatEntry(tempEntry);
            if (ed) {
              requestAnimationFrame(() => {
                ed.commands.setContent(tempEntry || "");
              });
            }
          } else {
            const newIdx = idx - 1;
            setHistoryIndex(newIdx);
            props.setChatEntry(history[newIdx]);
            if (ed) {
              requestAnimationFrame(() => {
                ed.commands.setContent(history[newIdx]);
              });
            }
          }
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const md = getEditorMarkdown(ed);
      props.setChatEntry(md);
      // Notify parent of input changes (used for typing indicators in team chat)
      if (props.onInputChange) {
        props.onInputChange(md);
      }
    },
  });

  // Expose editor to parent
  useEffect(() => {
    if (editor && props.editorRef && !editorCallbackFired.current) {
      props.editorRef.current = { editor };
      editorCallbackFired.current = true;
    }
  }, [editor, props.editorRef]);

  // Keep ref updated
  useEffect(() => {
    if (editor && props.editorRef) {
      props.editorRef.current = { editor };
    }
  }, [editor]);

  // Focus on thread change
  useEffect(() => {
    editor?.commands.focus();
  }, [props.currentThreadId, editor]);

  // Sync external chatEntry changes (e.g. from prompts loader) into editor
  const lastExternalSync = useRef("");
  useEffect(() => {
    if (!editor) return;
    const currentMd = getEditorMarkdown(editor);
    if (
      props.chatEntry !== currentMd &&
      props.chatEntry !== lastExternalSync.current
    ) {
      lastExternalSync.current = props.chatEntry;
      if (props.chatEntry === "") {
        editor.commands.clearContent();
        setHistoryIndex(-1);
        setTempEntry("");
      } else {
        editor.commands.setContent(props.chatEntry);
      }
    }
  }, [props.chatEntry, editor]);

  if (!editor) return null;

  return (
    <div className="tiptap-input">
      <EditorContent editor={editor} />
      <style>{`
        .tiptap-input .ProseMirror {
          min-height: 56px;
          max-height: 50vh;
          overflow-y: auto;
        }
        .tiptap-input .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #a1a1aa;
          pointer-events: none;
          height: 0;
        }
        .dark .tiptap-input .ProseMirror p.is-editor-empty:first-child::before {
          color: #52525b;
        }
        .tiptap-input .ProseMirror p {
          margin: 0;
        }
        .tiptap-input .ProseMirror strong {
          font-weight: 700;
        }
        .tiptap-input .ProseMirror em {
          font-style: italic;
        }
        .tiptap-input .ProseMirror code {
          background: rgb(228 228 231);
          padding: 1px 5px;
          border-radius: 4px;
          font-family: ui-monospace, monospace;
          font-size: 13px;
        }
        .dark .tiptap-input .ProseMirror code {
          background: rgb(39 39 42);
        }
        .tiptap-input .ProseMirror a {
          color: rgb(37 99 235);
          text-decoration: underline;
        }
        .dark .tiptap-input .ProseMirror a {
          color: rgb(96 165 250);
        }
        .tiptap-input .ProseMirror:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
};

export default TiptapInput;
